// Service de gestion des notifications système
import type { SystemNotification } from '../Core/types';

type NotificationListener = (notifications: SystemNotification[]) => void;

class SystemNotificationServiceClass {
    private notifications: SystemNotification[] = [];
    private listeners: Set<NotificationListener> = new Set();
    private notificationIdCounter = 0;

    /**
     * Ajoute une notification
     */
    notify(
        type: SystemNotification['type'],
        message: string,
        duration?: number
    ): string {
        const id = `notification-${++this.notificationIdCounter}-${Date.now()}`;
        
        const notification: SystemNotification = {
            id,
            type,
            message,
            duration: duration ?? (type === 'error' ? 10000 : 5000),
            timestamp: new Date()
        };

        this.notifications.push(notification);
        this.notifyListeners();

        // Auto-suppression après la durée
        if (notification.duration) {
            setTimeout(() => {
                this.dismiss(id);
            }, notification.duration);
        }

        return id;
    }

    /**
     * Raccourcis pour les différents types
     */
    error(message: string, duration?: number): string {
        return this.notify('error', message, duration);
    }

    warning(message: string, duration?: number): string {
        return this.notify('warning', message, duration);
    }

    info(message: string, duration?: number): string {
        return this.notify('info', message, duration);
    }

    success(message: string, duration?: number): string {
        return this.notify('success', message, duration);
    }

    /**
     * Supprime une notification
     */
    dismiss(id: string) {
        const index = this.notifications.findIndex(n => n.id === id);
        if (index !== -1) {
            this.notifications.splice(index, 1);
            this.notifyListeners();
        }
    }

    /**
     * Supprime toutes les notifications
     */
    clear() {
        this.notifications = [];
        this.notifyListeners();
    }

    /**
     * Récupère toutes les notifications
     */
    getAll(): SystemNotification[] {
        return [...this.notifications];
    }

    /**
     * S'abonne aux changements
     */
    subscribe(listener: NotificationListener): () => void {
        this.listeners.add(listener);
        // Retourne une fonction de désabonnement
        return () => {
            this.listeners.delete(listener);
        };
    }

    /**
     * Notifie tous les listeners
     */
    private notifyListeners() {
        const currentNotifications = this.getAll();
        this.listeners.forEach(listener => {
            listener(currentNotifications);
        });
    }
}

// Export singleton
export const SystemNotificationService = new SystemNotificationServiceClass();


